/**
 * Performance metrics: the shapes and pure helpers shared by the server
 * recorder (server/perf.ts), the client recorder (src/perf.ts), the summary
 * endpoint (functions/api/perf/summary.ts) and the workshop's Performance
 * section. See docs/performance.md.
 *
 * Dependency-free like shared/bounty.ts, so it runs on the server, in the
 * browser and in the Node test runner.
 */

// ---------- Server-side samples (perf_samples table, migration 0018) ----------

/** One API request (or one housekeeping pass) as recorded by server/perf.ts. */
export interface PerfSample {
  id: string;
  household_id: string;
  at: string;
  /** Templated route, e.g. "/api/tasks/:id"; housekeeping passes use "alarm" and "cron". */
  route: string;
  method: string;
  status: number;
  /** Wall time of the handler, ms. */
  ms: number;
  /** Number of D1 statements (a batch counts its statements). */
  d1_queries: number;
  /** Number of round trips to D1: one per run/all/first/raw call, one per batch call regardless of its statement count. */
  d1_round_trips: number;
  /** Wall time spent awaiting D1, ms (includes the round trip to the database). */
  d1_ms: number;
  /** D1's own execution time (meta.duration summed), ms; null when no statement reported it. */
  d1_exec_ms: number | null;
  d1_rows_read: number;
  d1_rows_written: number;
  /** Named sub-spans in ms, e.g. { housekeeping: 180, push: 40 }. */
  spans: Record<string, number>;
  /** Cloudflare colo that served the request, e.g. "YYZ"; empty when unknown. */
  colo: string;
}

// ---------- Client-side events (client_events rows, kind "perf.*") ----------

/** kind "perf.nav": once per page load, after the first paint metrics have settled. */
export interface PerfNavDetail {
  ttfb: number | null;
  fcp: number | null;
  lcp: number | null;
  dom_interactive: number | null;
  load: number | null;
  html_bytes: number | null;
  js_bytes: number | null;
  js_ms: number | null;
  /** True when the main bundle came from the service worker or HTTP cache (no bytes transferred). */
  js_cached: boolean | null;
  css_bytes: number | null;
  standalone: boolean;
  nav_type: string | null;
  connection: string | null;
}

/** kind "perf.api": one per route per flush, aggregated on the device. */
export interface PerfApiDetail {
  route: string;
  method: string;
  n: number;
  p50: number;
  p95: number;
  max: number;
  /** Server-reported handler time (Server-Timing app), p50; null when the header was absent. */
  server_p50: number | null;
  /** Client time minus server time, p50: the network's share. */
  overhead_p50: number | null;
  errors: number;
}

/** kind "perf.board": time from the board mounting to its first list render, aggregated. */
export interface PerfBoardDetail {
  status: "open" | "done";
  n: number;
  p50: number;
  p95: number;
  max: number;
}

/**
 * kind "perf.screen": time from a screen mounting to its content showing, aggregated per screen
 * key. The Hall of Fame reports "hall" (fetched on this visit) and "hall-remembered" (painted from
 * the months shown last time, shared/hall-memory.ts). `press_p50` is the time from the press that
 * opened the screen to its mount (the navigator's share); `fetch_p50` the wait for the request from
 * the screen's side (shorter than the request when a press-in prefetch started it ahead).
 */
export interface PerfScreenDetail {
  screen: string;
  n: number;
  p50: number;
  p95: number;
  max: number;
  press_p50: number | null;
  fetch_p50: number | null;
}

/** kind "perf.live": time from a live change notification to the refetch settling, aggregated. */
export interface PerfLiveDetail {
  n: number;
  p50: number;
  p95: number;
  max: number;
}

/** kind "perf.session": once per hide/unload, counters since the previous report. */
export interface PerfSessionDetail {
  duration_ms: number;
  longtasks: number;
  longtask_ms: number;
  longtask_max: number;
  /** Slowest interaction (event timing duration), ms; null when unsupported. */
  inp: number | null;
  heap_mb: number | null;
  ws_reconnects: number;
  ws_fallbacks: number;
}

// ---------- Summary (GET /api/perf/summary) ----------

export interface PerfStat {
  n: number;
  p50: number;
  p95: number;
  max: number;
  avg: number;
}

export interface PerfRouteSummary {
  route: string;
  method: string;
  n: number;
  p50: number;
  p95: number;
  max: number;
  avg_d1_queries: number;
  avg_d1_round_trips: number;
  avg_d1_ms: number;
  avg_d1_exec_ms: number | null;
  avg_rows_read: number;
  avg_rows_written: number;
  /** Responses with status >= 500. */
  errors: number;
}

export interface PerfSpanSummary {
  name: string;
  n: number;
  p50: number;
  p95: number;
}

/** Routes recorded as their own perf sample rather than a request (server/perf.ts sampleFromRecorder callers). Summarized separately from the routes table (T18 dashboard). */
export const JOB_ROUTES = ["housekeeping", "alarm", "cron", "push"] as const;
export type JobRoute = (typeof JOB_ROUTES)[number];

/** The four routes the workshop's headline tiles track, in display order. */
export const HEADLINE_ROUTES: { key: string; label: string; route: string; method: string }[] = [
  { key: "board_load", label: "Board load", route: "/api/board", method: "GET" },
  { key: "open_list", label: "Open list", route: "/api/tasks", method: "GET" },
  { key: "task_complete", label: "Task complete", route: "/api/tasks/:id", method: "PATCH" },
  { key: "photo_read", label: "Photo read", route: "/api/photos/:id", method: "GET" },
];

export interface PerfHeadlineTile {
  key: string;
  label: string;
  route: string;
  method: string;
  /** null when the window has no samples for this route. */
  n: number;
  p50: number | null;
  p95: number | null;
}

export interface PerfTrendBucket {
  /** ISO start of the bucket (hour or day, see PerfRouteTrend.granularity). */
  bucket: string;
  n: number;
  p50: number;
  p95: number;
  avg_d1_round_trips: number;
}

export interface PerfRouteTrend {
  route: string;
  method: string;
  granularity: "hour" | "day";
  points: PerfTrendBucket[];
}

/** Per colo (F12/T17): "D1 distance per trip" is median(d1_ms - d1_exec_ms) / round trips, the number T17's decision hinges on. */
export interface PerfColoDistance {
  colo: string;
  n: number;
  /** null when no sample in this colo reported both a round trip and an exec time. */
  distance_p50: number | null;
  p95_total: number;
}

/** Background jobs (cron, alarm, housekeeping, push), summarized like routes but kept separate from the request table. push's n/p50/p95 describe the fan-out call itself; avg_targets/avg_sent/avg_failed are push-only (0 for the others). */
export interface PerfJobSummary {
  route: JobRoute;
  method: string;
  n: number;
  p50: number;
  p95: number;
  avg_d1_queries: number;
  avg_d1_round_trips: number;
  avg_targets: number;
  avg_sent: number;
  avg_failed: number;
}

export interface PerfClientApiSummary {
  route: string;
  method: string;
  n: number;
  p50: number;
  p95: number;
  max: number;
  server_p50: number | null;
  overhead_p50: number | null;
  errors: number;
}

export interface PerfSummary {
  hours: number;
  from: string;
  to: string;
  /** T18: which store answered the server half. "d1" also when PERF_AE/CF_ANALYTICS_TOKEN aren't configured, or the AE query errored. */
  source: "ae" | "d1";
  /** Set (never the token) when source is "d1" because the AE query itself failed, as opposed to AE simply not being configured. */
  source_error?: string;
  /** True when the requested window was narrowed because the D1 fallback caps at 168 hours (server/perfAe.ts). */
  capped: boolean;
  server: {
    samples: number;
    routes: PerfRouteSummary[];
    spans: PerfSpanSummary[];
    colos: { colo: string; n: number }[];
    headline: PerfHeadlineTile[];
    trends: PerfRouteTrend[];
    colo_distance: PerfColoDistance[];
    jobs: PerfJobSummary[];
    /** Total non-job requests in the window (sum of routes[].n). */
    request_count: number;
    /** Share of request_count with status >= 500, 0..1; null when request_count is 0. */
    error_rate: number | null;
  };
  client: {
    /** Page loads that reported perf.nav in the window. */
    loads: number;
    ttfb: PerfStat | null;
    fcp: PerfStat | null;
    lcp: PerfStat | null;
    dom_interactive: PerfStat | null;
    js_bytes: PerfStat | null;
    /** Share of loads whose bundle came from a cache, 0..1; null when no load reported it. */
    js_cached_share: number | null;
    standalone_share: number | null;
    api: PerfClientApiSummary[];
    board_open: PerfStat | null;
    board_done: PerfStat | null;
    live: PerfStat | null;
    sessions: number;
    longtasks: PerfStat | null;
    inp: PerfStat | null;
    heap_mb: PerfStat | null;
    ws_reconnects: number;
    ws_fallbacks: number;
  };
}

// ---------- Pure helpers ----------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Collapses a request path to its route template so samples group: the
 * query string is dropped and any UUID segment becomes ":id".
 * "/api/tasks/3f2b...-...?tz=x" -> "/api/tasks/:id".
 */
export function templateRoute(path: string): string {
  const clean = path.split("?")[0].split("#")[0];
  return clean
    .split("/")
    .map((seg) => (UUID_RE.test(seg) ? ":id" : seg))
    .join("/");
}

/** Nearest-rank percentile of an ascending-sorted list (same rule as shared/taskstats.ts). 0 for an empty list. */
export function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const rank = Math.min(sorted.length, Math.max(1, Math.ceil((p / 100) * sorted.length)));
  return sorted[rank - 1];
}

/** n, p50, p95, max and mean of a list, or null when it is empty. Values are rounded to one decimal. */
export function stat(values: number[]): PerfStat | null {
  const nums = values.filter((v) => Number.isFinite(v));
  if (nums.length === 0) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const sum = sorted.reduce((s, v) => s + v, 0);
  return {
    n: sorted.length,
    p50: round1(percentile(sorted, 50)),
    p95: round1(percentile(sorted, 95)),
    max: round1(sorted[sorted.length - 1]),
    avg: round1(sum / sorted.length),
  };
}

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** The parts of a Server-Timing header this app emits: app (handler), d1 (awaiting D1), d1exec (D1's own time) and the query count. */
export interface ServerTiming {
  app: number | null;
  d1: number | null;
  d1exec: number | null;
  queries: number | null;
}

/**
 * Parses `Server-Timing: app;dur=123.4, d1;dur=45;desc="7q", d1exec;dur=12`.
 * Unknown metrics are ignored; a missing header gives all nulls.
 */
export function parseServerTiming(header: string | null | undefined): ServerTiming {
  const out: ServerTiming = { app: null, d1: null, d1exec: null, queries: null };
  if (!header) return out;
  for (const part of header.split(",")) {
    const [rawName, ...params] = part.trim().split(";");
    const name = rawName.trim();
    let dur: number | null = null;
    let desc: string | null = null;
    for (const p of params) {
      const [k, v] = p.trim().split("=");
      if (k === "dur") dur = Number(v);
      if (k === "desc") desc = (v ?? "").replace(/^"|"$/g, "");
    }
    if (dur !== null && !Number.isFinite(dur)) dur = null;
    if (name === "app") out.app = dur;
    else if (name === "d1") {
      out.d1 = dur;
      const m = desc?.match(/^(\d+)q$/);
      if (m) out.queries = Number(m[1]);
    } else if (name === "d1exec") out.d1exec = dur;
  }
  return out;
}

/** Builds the Server-Timing header value the server emits (the inverse of parseServerTiming). */
export function formatServerTiming(t: { app: number; d1: number; d1exec: number | null; queries: number }): string {
  const parts = [`app;dur=${round1(t.app)}`, `d1;dur=${round1(t.d1)};desc="${t.queries}q"`];
  if (t.d1exec !== null) parts.push(`d1exec;dur=${round1(t.d1exec)}`);
  return parts.join(", ");
}

/**
 * Keeps a bounded, evenly spread set of values per key (reservoir sampling)
 * plus exact count, max and error tallies, so a device can report a route's
 * distribution in one event instead of one event per request. Pure: no
 * timers or network. Used by src/perf.ts; tested in tests/perf.test.ts.
 */
export class Reservoir {
  private buckets = new Map<string, { values: number[]; seen: number; max: number; errors: number; extra: Map<string, number[]> }>();
  private readonly capacity: number;
  private readonly random: () => number;

  constructor(capacity = 32, random: () => number = Math.random) {
    this.capacity = capacity;
    this.random = random;
  }

  /** Adds `value` under `key`; `extra` carries parallel measurements (e.g. server time) sampled alongside. */
  add(key: string, value: number, opts: { error?: boolean; extra?: Record<string, number | null> } = {}): void {
    let b = this.buckets.get(key);
    if (!b) {
      b = { values: [], seen: 0, max: 0, errors: 0, extra: new Map() };
      this.buckets.set(key, b);
    }
    b.seen++;
    if (opts.error) b.errors++;
    b.max = Math.max(b.max, value);
    const keep = (list: number[], v: number) => {
      if (list.length < this.capacity) list.push(v);
      else {
        const i = Math.floor(this.random() * b!.seen);
        if (i < this.capacity) list[i] = v;
      }
    };
    keep(b.values, value);
    for (const [name, v] of Object.entries(opts.extra ?? {})) {
      if (v === null || !Number.isFinite(v)) continue;
      let list = b.extra.get(name);
      if (!list) {
        list = [];
        b.extra.set(name, list);
      }
      keep(list, v);
    }
  }

  get size(): number {
    return this.buckets.size;
  }

  /** Drains every key into a summary and resets. */
  flush(): { key: string; n: number; p50: number; p95: number; max: number; errors: number; extra: Record<string, number | null> }[] {
    const out: ReturnType<Reservoir["flush"]> = [];
    for (const [key, b] of this.buckets) {
      const sorted = [...b.values].sort((a, c) => a - c);
      const extra: Record<string, number | null> = {};
      for (const [name, list] of b.extra) extra[name] = list.length ? round1(percentile([...list].sort((a, c) => a - c), 50)) : null;
      out.push({ key, n: b.seen, p50: round1(percentile(sorted, 50)), p95: round1(percentile(sorted, 95)), max: round1(b.max), errors: b.errors, extra });
    }
    this.buckets.clear();
    return out;
  }
}

// ---------- Summary computation (pure; used by functions/api/perf/summary.ts) ----------

function finiteOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function asRecord(detail: unknown): Record<string, unknown> | null {
  return detail !== null && typeof detail === "object" ? (detail as Record<string, unknown>) : null;
}

/**
 * Expands each (value, weight) pair into up to `cap` copies of value and
 * takes the nearest-rank p50. Used to re-merge client-aggregated stats
 * (each event already summarizes its own n samples on the device) without
 * the raw samples: an approximation, but a stable and cheap one.
 */
function weightedMedian(pairs: { value: number; weight: number }[], cap = 100): number {
  const expanded: number[] = [];
  for (const { value, weight } of pairs) {
    const copies = Math.max(0, Math.min(Math.round(weight), cap));
    for (let i = 0; i < copies; i++) expanded.push(value);
  }
  if (expanded.length === 0) return 0;
  expanded.sort((a, b) => a - b);
  return round1(percentile(expanded, 50));
}

function weightedMedianOrNull(pairs: { value: number; weight: number }[], cap = 100): number | null {
  return pairs.length === 0 ? null : weightedMedian(pairs, cap);
}

function average(values: number[]): number {
  return values.length === 0 ? 0 : round1(values.reduce((s, v) => s + v, 0) / values.length);
}

function share(numerator: number, denominator: number): number | null {
  return denominator === 0 ? null : Math.round((numerator / denominator) * 1000) / 1000;
}

/** Splits a "route::method" grouping key back into its parts (routes never contain "::"). */
function splitRouteKey(key: string): [string, string] {
  const at = key.indexOf("::");
  return at === -1 ? [key, ""] : [key.slice(0, at), key.slice(at + 2)];
}

/** Floors an ISO timestamp to the start of its hour or day (UTC), for trend buckets. */
function bucketOf(at: string, granularity: "hour" | "day"): string {
  const d = new Date(at);
  if (granularity === "hour") d.setUTCMinutes(0, 0, 0);
  else d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}

/** Short windows get hourly trend buckets, long ones daily, so a 90-day chart isn't 2160 points. */
export function trendGranularity(hours: number): "hour" | "day" {
  return hours <= 48 ? "hour" : "day";
}

/**
 * Picks each of shared/perf.ts's HEADLINE_ROUTES out of an already-computed
 * routes table. Shared by both the D1 path (summarizeServerSamples below)
 * and the Analytics Engine path (server/perfAe.ts), since both produce the
 * same PerfRouteSummary[] shape.
 */
export function buildHeadline(routes: PerfRouteSummary[]): PerfHeadlineTile[] {
  return HEADLINE_ROUTES.map((h) => {
    const found = routes.find((r) => r.route === h.route && r.method === h.method);
    return { key: h.key, label: h.label, route: h.route, method: h.method, n: found?.n ?? 0, p50: found?.p50 ?? null, p95: found?.p95 ?? null };
  });
}

/**
 * Builds the server half of /api/perf/summary from perf_samples rows (the D1
 * path; server/perfAe.ts builds the equivalent from Analytics Engine query
 * results). Pure: no I/O, no Date.now(), no randomness, so it is fully
 * covered by tests/perf.test.ts. Background-job rows (JOB_ROUTES: alarm,
 * cron, housekeeping, push) are split out into `jobs` and excluded from
 * `routes`/`spans`/`request_count`/`error_rate`/`colo_distance`, matching the
 * "background jobs summarized separately" requirement (T18 dashboard).
 */
export function summarizeServerSamples(samples: PerfSample[], window: { hours: number; from: string; to: string }): PerfSummary["server"] {
  const jobRouteSet = new Set<string>(JOB_ROUTES);
  const requestSamples = samples.filter((s) => !jobRouteSet.has(s.route));
  const jobSamples = samples.filter((s) => jobRouteSet.has(s.route));

  // ---- routes ----
  const routeGroups = new Map<string, PerfSample[]>();
  for (const s of requestSamples) {
    const key = `${s.route}::${s.method}`;
    const group = routeGroups.get(key);
    if (group) group.push(s);
    else routeGroups.set(key, [s]);
  }
  const routes: PerfRouteSummary[] = [...routeGroups.values()]
    .map((group) => {
      const ms = stat(group.map((s) => s.ms))!;
      const execValues = group.map((s) => s.d1_exec_ms).filter((v): v is number => v !== null);
      return {
        route: group[0].route,
        method: group[0].method,
        n: ms.n,
        p50: ms.p50,
        p95: ms.p95,
        max: ms.max,
        avg_d1_queries: average(group.map((s) => s.d1_queries)),
        avg_d1_round_trips: average(group.map((s) => s.d1_round_trips)),
        avg_d1_ms: average(group.map((s) => s.d1_ms)),
        avg_d1_exec_ms: execValues.length ? average(execValues) : null,
        avg_rows_read: average(group.map((s) => s.d1_rows_read)),
        avg_rows_written: average(group.map((s) => s.d1_rows_written)),
        errors: group.filter((s) => s.status >= 500).length,
      };
    })
    .sort((a, b) => b.n - a.n);

  // ---- spans ----
  const spanGroups = new Map<string, number[]>();
  for (const s of requestSamples) {
    for (const [name, value] of Object.entries(s.spans)) {
      if (typeof value !== "number" || !Number.isFinite(value)) continue;
      const list = spanGroups.get(name);
      if (list) list.push(value);
      else spanGroups.set(name, [value]);
    }
  }
  const spans: PerfSpanSummary[] = [...spanGroups.entries()]
    .map(([name, values]) => {
      const st = stat(values)!;
      return { name, n: st.n, p50: st.p50, p95: st.p95 };
    })
    .sort((a, b) => b.n - a.n);

  // ---- colos (all samples, background jobs included: "served from" is about the whole deployment) ----
  const coloCounts = new Map<string, number>();
  for (const s of samples) {
    if (!s.colo) continue;
    coloCounts.set(s.colo, (coloCounts.get(s.colo) ?? 0) + 1);
  }
  const colos = [...coloCounts.entries()].map(([colo, n]) => ({ colo, n })).sort((a, b) => b.n - a.n);

  // ---- colo distance (F12/T17): request samples only, median(d1_ms - d1_exec_ms) / round trips ----
  const coloGroups = new Map<string, PerfSample[]>();
  for (const s of requestSamples) {
    if (!s.colo) continue;
    const group = coloGroups.get(s.colo);
    if (group) group.push(s);
    else coloGroups.set(s.colo, [s]);
  }
  const colo_distance: PerfColoDistance[] = [...coloGroups.entries()]
    .map(([colo, group]) => {
      const total = stat(group.map((s) => s.ms))!;
      const distances = group
        .filter((s) => s.d1_exec_ms !== null && s.d1_round_trips > 0)
        .map((s) => (s.d1_ms - (s.d1_exec_ms as number)) / s.d1_round_trips)
        .sort((a, b) => a - b);
      return { colo, n: total.n, distance_p50: distances.length ? round1(percentile(distances, 50)) : null, p95_total: total.p95 };
    })
    .sort((a, b) => b.n - a.n);

  // ---- trend: p50/p95/round-trips per route+method, bucketed by hour or day ----
  const granularity = trendGranularity(window.hours);
  const trendGroups = new Map<string, Map<string, PerfSample[]>>();
  for (const s of requestSamples) {
    const routeKey = `${s.route}::${s.method}`;
    const bucket = bucketOf(s.at, granularity);
    let byBucket = trendGroups.get(routeKey);
    if (!byBucket) {
      byBucket = new Map();
      trendGroups.set(routeKey, byBucket);
    }
    const list = byBucket.get(bucket);
    if (list) list.push(s);
    else byBucket.set(bucket, [s]);
  }
  const trends: PerfRouteTrend[] = [...trendGroups.entries()]
    .map(([routeKey, byBucket]) => {
      const [route, method] = splitRouteKey(routeKey);
      const points: PerfTrendBucket[] = [...byBucket.entries()]
        .map(([bucket, group]) => {
          const st = stat(group.map((s) => s.ms))!;
          return { bucket, n: st.n, p50: st.p50, p95: st.p95, avg_d1_round_trips: average(group.map((s) => s.d1_round_trips)) };
        })
        .sort((a, b) => (a.bucket < b.bucket ? -1 : a.bucket > b.bucket ? 1 : 0));
      return { route, method, granularity, points, total: points.reduce((sum, p) => sum + p.n, 0) };
    })
    .sort((a, b) => b.total - a.total)
    .map(({ total: _total, ...rest }) => rest);

  // ---- background jobs (alarm, cron, housekeeping, push) ----
  const jobGroups = new Map<string, PerfSample[]>();
  for (const s of jobSamples) {
    const key = `${s.route}::${s.method}`;
    const group = jobGroups.get(key);
    if (group) group.push(s);
    else jobGroups.set(key, [s]);
  }
  const spanAvg = (group: PerfSample[], name: string): number => average(group.map((s) => (typeof s.spans[name] === "number" ? s.spans[name] : 0)));
  const jobs: PerfJobSummary[] = [...jobGroups.values()]
    .map((group) => {
      const ms = stat(group.map((s) => s.ms))!;
      return {
        route: group[0].route as JobRoute,
        method: group[0].method,
        n: ms.n,
        p50: ms.p50,
        p95: ms.p95,
        avg_d1_queries: average(group.map((s) => s.d1_queries)),
        avg_d1_round_trips: average(group.map((s) => s.d1_round_trips)),
        avg_targets: spanAvg(group, "targets"),
        avg_sent: spanAvg(group, "sent"),
        avg_failed: spanAvg(group, "failed"),
      };
    })
    .sort((a, b) => b.n - a.n);

  const request_count = requestSamples.length;
  const errorCount = requestSamples.filter((s) => s.status >= 500).length;

  return {
    samples: samples.length,
    routes,
    spans,
    colos,
    headline: buildHeadline(routes),
    trends,
    colo_distance,
    jobs,
    request_count,
    error_rate: share(errorCount, request_count),
  };
}

/**
 * Builds the client half of /api/perf/summary from the perf.* client_events
 * rows in a window (both the D1 and Analytics Engine server paths share this:
 * client events always live in D1's client_events table). Pure; A malformed
 * detail object (a device running mismatched code) is skipped rather than
 * thrown on; it just doesn't contribute to the summary.
 */
export function summarizeClientEvents(clientEvents: { kind: string; detail: unknown }[]): PerfSummary["client"] {
  // ---- client: perf.nav ----
  let loads = 0;
  const ttfbs: number[] = [];
  const fcps: number[] = [];
  const lcps: number[] = [];
  const doms: number[] = [];
  const jsBytesList: number[] = [];
  let jsCachedYes = 0;
  let jsCachedTotal = 0;
  let standaloneYes = 0;

  interface ApiEventLite {
    route: string;
    method: string;
    n: number;
    p50: number;
    p95: number;
    max: number;
    server_p50: number | null;
    overhead_p50: number | null;
    errors: number;
  }
  const apiEvents: ApiEventLite[] = [];

  interface AggEventLite {
    status: string;
    n: number;
    p50: number;
    p95: number;
    max: number;
  }
  const boardEvents: AggEventLite[] = [];
  const liveEvents: Omit<AggEventLite, "status">[] = [];

  let sessions = 0;
  const longtaskCounts: number[] = [];
  const inps: number[] = [];
  const heapMbs: number[] = [];
  let wsReconnects = 0;
  let wsFallbacks = 0;

  for (const e of clientEvents) {
    const d = asRecord(e.detail);
    if (!d) continue;
    switch (e.kind) {
      case "perf.nav": {
        loads++;
        const ttfb = finiteOrNull(d.ttfb);
        if (ttfb !== null) ttfbs.push(ttfb);
        const fcp = finiteOrNull(d.fcp);
        if (fcp !== null) fcps.push(fcp);
        const lcp = finiteOrNull(d.lcp);
        if (lcp !== null) lcps.push(lcp);
        const dom = finiteOrNull(d.dom_interactive);
        if (dom !== null) doms.push(dom);
        const jsBytes = finiteOrNull(d.js_bytes);
        if (jsBytes !== null) jsBytesList.push(jsBytes);
        if (d.js_cached === true) {
          jsCachedYes++;
          jsCachedTotal++;
        } else if (d.js_cached === false) {
          jsCachedTotal++;
        }
        if (d.standalone === true) standaloneYes++;
        break;
      }
      case "perf.api": {
        const route = typeof d.route === "string" ? d.route : null;
        const method = typeof d.method === "string" ? d.method : null;
        const n = finiteOrNull(d.n);
        const p50 = finiteOrNull(d.p50);
        const p95 = finiteOrNull(d.p95);
        const max = finiteOrNull(d.max);
        const errors = finiteOrNull(d.errors);
        if (route === null || method === null || n === null || p50 === null || p95 === null || max === null || errors === null) break;
        apiEvents.push({ route, method, n, p50, p95, max, server_p50: finiteOrNull(d.server_p50), overhead_p50: finiteOrNull(d.overhead_p50), errors });
        break;
      }
      case "perf.board": {
        const status = typeof d.status === "string" ? d.status : null;
        const n = finiteOrNull(d.n);
        const p50 = finiteOrNull(d.p50);
        const p95 = finiteOrNull(d.p95);
        const max = finiteOrNull(d.max);
        if (status === null || n === null || p50 === null || p95 === null || max === null) break;
        boardEvents.push({ status, n, p50, p95, max });
        break;
      }
      case "perf.live": {
        const n = finiteOrNull(d.n);
        const p50 = finiteOrNull(d.p50);
        const p95 = finiteOrNull(d.p95);
        const max = finiteOrNull(d.max);
        if (n === null || p50 === null || p95 === null || max === null) break;
        liveEvents.push({ n, p50, p95, max });
        break;
      }
      case "perf.session": {
        const longtasks = finiteOrNull(d.longtasks);
        if (longtasks === null) break;
        sessions++;
        longtaskCounts.push(longtasks);
        const inp = finiteOrNull(d.inp);
        if (inp !== null) inps.push(inp);
        const heap = finiteOrNull(d.heap_mb);
        if (heap !== null) heapMbs.push(heap);
        wsReconnects += finiteOrNull(d.ws_reconnects) ?? 0;
        wsFallbacks += finiteOrNull(d.ws_fallbacks) ?? 0;
        break;
      }
      default:
        break;
    }
  }

  // ---- client: perf.api, merged per route+method ----
  const apiGroups = new Map<string, ApiEventLite[]>();
  for (const ev of apiEvents) {
    const key = `${ev.route}::${ev.method}`;
    const group = apiGroups.get(key);
    if (group) group.push(ev);
    else apiGroups.set(key, [ev]);
  }
  const api: PerfClientApiSummary[] = [...apiGroups.values()]
    .map((group) => {
      const serverPairs = group.filter((e) => e.server_p50 !== null).map((e) => ({ value: e.server_p50 as number, weight: e.n }));
      const overheadPairs = group.filter((e) => e.overhead_p50 !== null).map((e) => ({ value: e.overhead_p50 as number, weight: e.n }));
      return {
        route: group[0].route,
        method: group[0].method,
        n: group.reduce((s, e) => s + e.n, 0),
        p50: weightedMedian(group.map((e) => ({ value: e.p50, weight: e.n }))),
        p95: weightedMedian(group.map((e) => ({ value: e.p95, weight: e.n }))),
        max: round1(Math.max(...group.map((e) => e.max))),
        server_p50: weightedMedianOrNull(serverPairs),
        overhead_p50: weightedMedianOrNull(overheadPairs),
        errors: group.reduce((s, e) => s + e.errors, 0),
      };
    })
    .sort((a, b) => b.n - a.n);

  /**
   * Merges a set of already-aggregated events (perf.board/perf.live) into
   * one PerfStat via the same weighted-median approach as the api merge.
   * `avg` is set equal to `p50`: these are device-side aggregates, not raw
   * samples, so there is no true mean left to recompute; p50 is the closest
   * stand-in and keeps the shape uniform with the rest of PerfSummary.
   */
  function mergeStat(events: { n: number; p50: number; p95: number; max: number }[]): PerfStat | null {
    if (events.length === 0) return null;
    const p50 = weightedMedian(events.map((e) => ({ value: e.p50, weight: e.n })));
    return {
      n: events.reduce((s, e) => s + e.n, 0),
      p50,
      p95: weightedMedian(events.map((e) => ({ value: e.p95, weight: e.n }))),
      max: round1(Math.max(...events.map((e) => e.max))),
      avg: p50,
    };
  }

  const board_open = mergeStat(boardEvents.filter((e) => e.status === "open"));
  const board_done = mergeStat(boardEvents.filter((e) => e.status === "done"));
  const live = mergeStat(liveEvents);

  return {
    loads,
    ttfb: stat(ttfbs),
    fcp: stat(fcps),
    lcp: stat(lcps),
    dom_interactive: stat(doms),
    js_bytes: stat(jsBytesList),
    js_cached_share: share(jsCachedYes, jsCachedTotal),
    standalone_share: share(standaloneYes, loads),
    api,
    board_open,
    board_done,
    live,
    sessions,
    longtasks: stat(longtaskCounts),
    inp: stat(inps),
    heap_mb: stat(heapMbs),
    ws_reconnects: wsReconnects,
    ws_fallbacks: wsFallbacks,
  };
}

/**
 * Builds the full /api/perf/summary response for the D1 path: samples straight
 * from perf_samples, no Analytics Engine involved. `source` is always "d1"
 * here; functions/api/perf/summary.ts overwrites `source`/`source_error`/
 * `capped` on the object it gets back when this is being used as the AE
 * path's fallback. Pure: no I/O, no Date.now(), no randomness, so it is fully
 * covered by tests/perf.test.ts.
 */
export function summarizePerf(
  samples: PerfSample[],
  clientEvents: { kind: string; detail: unknown }[],
  window: { hours: number; from: string; to: string },
): PerfSummary {
  return {
    hours: window.hours,
    from: window.from,
    to: window.to,
    source: "d1",
    capped: false,
    server: summarizeServerSamples(samples, window),
    client: summarizeClientEvents(clientEvents),
  };
}
